import { ViewPreset as SchedulerViewPreset } from '@bryntum/schedulerpro';
import moment from 'moment';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { unreachable } from 'utils/unreachable';
import {
  isDateBetweenRange,
  shiftDateIfOnNonWorkingDay,
} from '../../utils/dates';
import { ViewPresetId } from './constants';

export type ViewPreset = Partial<SchedulerViewPreset> & {
  base?: string; // need to remove this after we extend from existing preset / create our own preset
  incrementBy?: string; // added to calculate split position.
};

const MonthViewPreset = () => ({
  base: 'monthAndYear',
  incrementBy: 'week',
  timeResolution: {
    unit: 'day',
    increment: 1,
  },
  tickWidth: 12,
  columnLinesFor: 2,
  shiftUnit: 'month',
  shiftIncrement: 1,
  headers: [
    {
      unit: 'year',
      increment: 1,
      align: 'start',
      renderer: (startDate: Date) => {
        return `<strong>${moment(startDate).format('YYYY')}</strong>`;
      },
    },
    {
      unit: 'month',
      increment: 1,
      align: 'start',
      renderer: (startDate: Date, endDate: Date, config) => {
        if (
          isDateBetweenRange(moment(), {
            startDate: moment(startDate),
            endDate: moment(endDate).subtract(1, 'minute'),
          })
        ) {
          // eslint-disable-next-line no-param-reassign
          config.headerCellCls = 'current-day';
        }

        return moment(startDate).format('MMM');
      },
    },
    {
      unit: 'week',
      increment: 1,
    },
    {
      unit: 'day',
      increment: 1,
    },
  ],
});
const WeekViewPreset = (workingDaysConfig: WorkingDaysConfiguration) => ({
  base: 'monthAndYear',
  incrementBy: 'day',
  timeResolution: {
    unit: 'day',
    increment: 1,
  },
  tickWidth: 24,
  shiftUnit: 'week',
  shiftIncrement: 1,
  columnLinesFor: 3,
  headers: [
    {
      unit: 'year',
      increment: 1,
    },
    {
      unit: 'month',
      increment: 1,
      align: 'start',
      renderer: (startDate: Date) => {
        return `<strong>${moment(startDate).format("MMM 'YY")}</strong>`;
      },
    },
    {
      unit: 'week',
      increment: 1,
      align: 'start',
      renderer: (startDate: Date, endDate: Date, config) => {
        if (
          isDateBetweenRange(moment(), {
            startDate: moment(startDate),
            endDate: moment(endDate).subtract(1, 'minute'),
          })
        ) {
          // eslint-disable-next-line no-param-reassign
          config.headerCellCls = 'current-day';
        }

        return `${shiftDateIfOnNonWorkingDay(
          moment(startDate),
          workingDaysConfig,
        ).format('Do')}-${shiftDateIfOnNonWorkingDay(
          moment(endDate),
          workingDaysConfig,
          false,
        ).format('Do')}`;
      },
    },
    {
      unit: 'day',
      increment: 1,
    },
  ],
});
const DayViewPreset = () => ({
  incrementBy: 'day',
  timeResolution: {
    unit: 'day',
    increment: 1,
  },
  rowHeight: 48,
  tickWidth: 72,
  shiftUnit: 'week',
  shiftIncrement: 1,
  headers: [
    {
      unit: 'month',
      increment: 1,
      align: 'start',
      renderer: (startDate: Date) => {
        return `<strong>${moment(startDate).format("MMM 'YY")}</strong>`;
      },
    },
    {
      unit: 'day',
      increment: 1,
      align: 'start',
      renderer: (startDate: Date, endDate: Date, config) => {
        if (moment().isSame(moment(startDate), 'day')) {
          // eslint-disable-next-line no-param-reassign
          config.headerCellCls = 'current-day';
        }

        return moment(startDate).format('ddd D');
      },
    },
  ],
});

export const getZoomPresets = (
  workingDaysConfig: WorkingDaysConfiguration,
): ViewPreset[] => [
  {
    id: ViewPresetId.Month,
    ...MonthViewPreset(),
  },
  {
    id: ViewPresetId.MonthZoomed,
    ...MonthViewPreset(),
    tickWidth: 18,
  },
  {
    id: ViewPresetId.Week,
    ...WeekViewPreset(workingDaysConfig),
  },
  {
    id: ViewPresetId.WeekZoomed,
    ...WeekViewPreset(workingDaysConfig),
    tickWidth: 36,
  },
  {
    id: ViewPresetId.Day,
    ...DayViewPreset(),
  },
  {
    id: ViewPresetId.DayZoomed,
    ...DayViewPreset(),
    tickWidth: 104,
  },
];

export const getZoomPresetsForHoursPerWeek = (
  workingDaysConfig: WorkingDaysConfiguration,
): ViewPreset[] => getZoomPresets(workingDaysConfig).slice(0, 4);

export const UserSelectablePresetIds = [
  ViewPresetId.Month,
  ViewPresetId.Week,
  ViewPresetId.Day,
];

export const UserSelectablePresetIdsForHoursPerWeek =
  UserSelectablePresetIds.slice(0, 2);

export const getUserSelectablePresetId = (id: ViewPresetId): ViewPresetId => {
  switch (id) {
    case ViewPresetId.Month:
    case ViewPresetId.MonthZoomed:
      return ViewPresetId.Month;
    case ViewPresetId.Week:
    case ViewPresetId.WeekZoomed:
      return ViewPresetId.Week;
    case ViewPresetId.Day:
    case ViewPresetId.DayZoomed:
      return ViewPresetId.Day;
    default:
      return unreachable(id);
  }
};

export const getMinimumNumberOfDaysForTextToBeVisible = (
  id: ViewPresetId,
): number => {
  switch (id) {
    case ViewPresetId.Month:
      return 5;
    case ViewPresetId.MonthZoomed:
      return 4;
    case ViewPresetId.Week:
      return 3;
    case ViewPresetId.WeekZoomed:
      return 2;
    case ViewPresetId.Day:
    case ViewPresetId.DayZoomed:
      return 0;
    default:
      return unreachable(id);
  }
};

export const getMinimumDaysForMoreActionsVisibility = (
  id: ViewPresetId,
): number => {
  switch (id) {
    case ViewPresetId.Month:
      return 4;
    case ViewPresetId.MonthZoomed:
      return 3;
    case ViewPresetId.Week:
    case ViewPresetId.WeekZoomed:
      return 2;
    case ViewPresetId.Day:
    case ViewPresetId.DayZoomed:
      return 0;
    default:
      return unreachable(id);
  }
};

export const getMinimumDaysForDragAffordanceVisibility = (
  id: ViewPresetId,
): number => {
  switch (id) {
    case ViewPresetId.Month:
    case ViewPresetId.MonthZoomed:
      return 2;
    case ViewPresetId.Week:
    case ViewPresetId.WeekZoomed:
    case ViewPresetId.Day:
    case ViewPresetId.DayZoomed:
      return 1;
    default:
      return unreachable(id);
  }
};

export const ValidViewPresets = [
  ViewPresetId.Week,
  ViewPresetId.Month,
  ViewPresetId.MonthZoomed,
  ViewPresetId.WeekZoomed,
  ViewPresetId.Day,
  ViewPresetId.DayZoomed,
];

export const ValidViewPresetsHoursPerWeek = [
  ViewPresetId.Week,
  ViewPresetId.Month,
  ViewPresetId.MonthZoomed,
  ViewPresetId.WeekZoomed,
];
